import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BirthdayEntry,
  CommunicationAutomationView,
  CommunicationDeliveryMode,
  CommunicationPurpose,
  CommunicationSummaryCounts,
  CommunicationTemplateDetail,
  CommunicationTemplateSummary,
  ContactPreferenceView,
  CustomerCommunicationDetail,
  CustomerCommunicationStatus,
  CustomerCommunicationSummary,
  CustomerReminderType,
  CustomerReminderView,
  PaginatedResult,
  PreferenceChangeView,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';

/**
 * Filters the communications workspace sends.
 *
 * Deliberately nothing free-text: there is no body search and no phone search, here or on the
 * server. A message's words and a customer's number are the two things that must never travel in a
 * query string, and the surest way to keep them out is to have no parameter that could carry them.
 */
export interface CommunicationListParams {
  page?: number;
  pageSize?: number;
  customerId?: string;
  branchId?: string;
  purpose?: CommunicationPurpose | '';
  status?: CustomerCommunicationStatus | '';
  deliveryMode?: CommunicationDeliveryMode | '';
  dateFrom?: string;
  dateTo?: string;
}

export const crmKeys = {
  all: ['crm'] as const,
  communications: () => [...crmKeys.all, 'communications'] as const,
  communicationList: (params: CommunicationListParams) =>
    [...crmKeys.communications(), 'list', params] as const,
  communicationSummary: (params: CommunicationListParams) =>
    [...crmKeys.communications(), 'summary', params] as const,
  communication: (id: string) => [...crmKeys.communications(), 'detail', id] as const,
  customerCommunications: (customerId: string) =>
    [...crmKeys.communications(), 'customer', customerId] as const,
  templates: () => [...crmKeys.all, 'templates'] as const,
  templateList: (params: { active?: boolean }) => [...crmKeys.templates(), 'list', params] as const,
  template: (id: string) => [...crmKeys.templates(), 'detail', id] as const,
  automations: () => [...crmKeys.all, 'automations'] as const,
  automation: (branchId: string | null) =>
    [...crmKeys.automations(), branchId ?? 'tenant'] as const,
  reminders: () => [...crmKeys.all, 'reminders'] as const,
  reminderList: (params: ReminderListParams) => [...crmKeys.reminders(), 'list', params] as const,
  birthdays: (params: { withinDays?: number }) => [...crmKeys.all, 'birthdays', params] as const,
  preferences: (customerId: string) => [...crmKeys.all, 'preferences', customerId] as const,
  preferenceHistory: (customerId: string) =>
    [...crmKeys.all, 'preferences', customerId, 'history'] as const,
};

/** Only non-empty filters are sent, so an untouched control never narrows the server's answer. */
function communicationParams(params: CommunicationListParams): Record<string, unknown> {
  return {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    ...(params.customerId ? { customerId: params.customerId } : {}),
    ...(params.branchId ? { branchId: params.branchId } : {}),
    ...(params.purpose ? { purpose: params.purpose } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.deliveryMode ? { deliveryMode: params.deliveryMode } : {}),
    ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
    ...(params.dateTo ? { dateTo: params.dateTo } : {}),
  };
}

// ---------------------------------------------------------------------------
// Communications
// ---------------------------------------------------------------------------

export function useCommunicationsQuery(
  params: CommunicationListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: crmKeys.communicationList(params),
    queryFn: () =>
      api.get<PaginatedResult<CustomerCommunicationSummary>>('/crm/communications', {
        params: communicationParams(params),
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The header counts, taking the same filters so they always describe what is on screen. */
export function useCommunicationSummaryQuery(
  params: CommunicationListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: crmKeys.communicationSummary(params),
    queryFn: () =>
      api.get<CommunicationSummaryCounts>('/crm/communications/summary', {
        params: communicationParams({ ...params, page: 1, pageSize: 1 }),
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useCommunicationQuery(id: string | null) {
  return useQuery({
    queryKey: crmKeys.communication(id ?? ''),
    queryFn: () => api.get<CustomerCommunicationDetail>(`/crm/communications/${id}`),
    enabled: Boolean(id),
  });
}

export function useCustomerCommunicationsQuery(
  customerId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: crmKeys.customerCommunications(customerId),
    queryFn: () =>
      api.get<PaginatedResult<CustomerCommunicationSummary>>(
        `/crm/customers/${customerId}/communications`,
        { params: { page: 1, pageSize: 20 } },
      ),
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

/** `active` is left unset on purpose: the management screen needs withdrawn templates too. */
export function useTemplatesQuery(params: { active?: boolean } = {}) {
  return useQuery({
    queryKey: crmKeys.templateList(params),
    queryFn: () =>
      api.get<PaginatedResult<CommunicationTemplateSummary>>('/crm/templates', {
        params: {
          page: 1,
          pageSize: 100,
          ...(params.active === undefined ? {} : { active: params.active }),
        },
      }),
  });
}

export function useTemplateQuery(id: string | null) {
  return useQuery({
    queryKey: crmKeys.template(id ?? ''),
    queryFn: () => api.get<CommunicationTemplateDetail>(`/crm/templates/${id}`),
    enabled: Boolean(id),
  });
}

export interface TemplateInput {
  code?: string;
  name: string;
  nameAr?: string | null;
  purpose?: CommunicationPurpose;
  bodyEn: string;
  bodyAr: string;
  version?: number;
}

/** Creating is idempotent on the server, so a retry after a lost response cannot leave two templates competing for one code; a wording change is guarded by its version instead. */
export function useSaveTemplateMutation(id: string | null) {
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: crmKeys.templates() });
    void client.invalidateQueries({ queryKey: crmKeys.automations() });
  };
  const create = useIdempotentMutation<CommunicationTemplateDetail, TemplateInput>(
    (input, config) => api.post<CommunicationTemplateDetail>('/crm/templates', input, config),
    { onSuccess: invalidate },
  );
  const update = useMutation<CommunicationTemplateDetail, unknown, TemplateInput>({
    mutationFn: (input) => api.put<CommunicationTemplateDetail>(`/crm/templates/${id}`, input),
    onSuccess: invalidate,
  });
  return id ? update : create;
}

export function useTemplateActivationMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      api.post<CommunicationTemplateDetail>(
        `/crm/templates/${id}/${active ? 'reactivate' : 'deactivate'}`,
        { version },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: crmKeys.templates() });
      void client.invalidateQueries({ queryKey: crmKeys.automations() });
    },
  });
}

// ---------------------------------------------------------------------------
// Automations
// ---------------------------------------------------------------------------

export function useAutomationQuery(branchId: string | null) {
  return useQuery({
    queryKey: crmKeys.automation(branchId),
    queryFn: () =>
      api.get<CommunicationAutomationView | null>(
        branchId
          ? `/crm/automations/ready-for-collection/branches/${branchId}`
          : '/crm/automations/ready-for-collection',
      ),
  });
}

export function useSaveAutomationMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      branchId?: string | null;
      enabled: boolean;
      templateId: string | null;
      version: number;
    }) => api.patch<CommunicationAutomationView>('/crm/automations/ready-for-collection', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: crmKeys.automations() });
    },
  });
}

// ---------------------------------------------------------------------------
// Reminders
// ---------------------------------------------------------------------------

export interface ReminderListParams {
  page?: number;
  pageSize?: number;
  customerId?: string;
  branchId?: string;
  status?: 'OPEN' | 'COMPLETED' | 'CANCELLED' | '';
  type?: CustomerReminderType | '';
}

export function useRemindersQuery(
  params: ReminderListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: crmKeys.reminderList(params),
    queryFn: () =>
      api.get<PaginatedResult<CustomerReminderView>>('/crm/reminders', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.customerId ? { customerId: params.customerId } : {}),
          ...(params.branchId ? { branchId: params.branchId } : {}),
          ...(params.status ? { status: params.status } : {}),
          ...(params.type ? { type: params.type } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useReminderActionMutation() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'complete' | 'cancel' }) =>
      api.post<CustomerReminderView>(`/crm/reminders/${id}/${action}`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: crmKeys.reminders() });
    },
  });
}

export interface ReminderInput {
  customerId: string;
  branchId: string;
  type: CustomerReminderType;
  dueDate: string;
  note?: string;
}

/** Idempotent on the server: a retried save after a lost response books the reminder once. */
export function useCreateReminderMutation() {
  const client = useQueryClient();

  return useIdempotentMutation<CustomerReminderView, ReminderInput>(
    (input, config) => api.post<CustomerReminderView>('/crm/reminders', input, config),
    {
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: crmKeys.reminders() });
      },
    },
  );
}

// ---------------------------------------------------------------------------
// Birthdays
// ---------------------------------------------------------------------------

export function useBirthdaysQuery(withinDays: number) {
  return useQuery({
    queryKey: crmKeys.birthdays({ withinDays }),
    queryFn: () => api.get<BirthdayEntry[]>('/crm/birthdays', { params: { withinDays } }),
  });
}

// ---------------------------------------------------------------------------
// Contact preferences
// ---------------------------------------------------------------------------

export function useContactPreferencesQuery(customerId: string, enabled = true) {
  return useQuery({
    queryKey: crmKeys.preferences(customerId),
    queryFn: () => api.get<ContactPreferenceView>(`/customers/${customerId}/contact-preferences`),
    enabled,
  });
}

/** The history route answers with a bare array, not a paginated envelope. */
export function usePreferenceHistoryQuery(customerId: string, enabled = true) {
  return useQuery({
    queryKey: crmKeys.preferenceHistory(customerId),
    queryFn: () =>
      api.get<PreferenceChangeView[]>(`/customers/${customerId}/contact-preferences/history`),
    enabled,
  });
}

export function useSavePreferencesMutation(customerId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      operationalMessagingSuppressed?: boolean;
      marketingConsentStatus?: string;
      version: number;
    }) => api.patch<ContactPreferenceView>(`/customers/${customerId}/contact-preferences`, input),
    onSuccess: (saved) => {
      client.setQueryData(crmKeys.preferences(customerId), saved);
      void client.invalidateQueries({ queryKey: crmKeys.preferenceHistory(customerId) });
    },
  });
}
